import moment from 'moment';
import Vue from 'vue';
import EcsState from '@/components/ecs-agent-state/EcsState';
import { agentSubscriptions } from '@/constants/kpi-counters/subscriptions';
import EcsAgentStatsMath from '@/components/ecs-agent-stats/EcsAgentStats.math';

const updateAgentsStats = (state, agents) => {
  // 1. Remove all agent stats without loosing array reference (To avoid loosing Reactivity)
  state.AGENTS.splice(-state.AGENTS.length);
  // 2. Push all new data.
  state.AGENTS.push(...agents);
};

const agentIndex = {};
const setAgentStatValue = (state, data) => {
  let index = agentIndex[data.agentId];

  if (index === undefined) {
    index = state.AGENTS.findIndex(({ agentId }) => agentId === data.agentId);
    agentIndex[data.agentId] = index;
  }

  if (index === -1) {
    return;
  }

  state.AGENTS[index][data.field] = data.value;
};

const businessProcessIndex = {};
const setBusinessProcessStatValue = (state, data) => {
  let index = businessProcessIndex[data.businessProcessId];

  if (index === undefined) {
    index = state.BUSINESS_PROCESSES.findIndex(({ businessProcessId }) => businessProcessId === data.businessProcessId);
    businessProcessIndex[data.businessProcessId] = index;
  }

  if (index === -1) {
    return;
  }

  state.BUSINESS_PROCESSES[index][data.field] = data.value;
};

const setLastKnownAgentStateChangeTimestamp = (state, event) => {
  const agentState = new EcsState(event);

  if (agentState.isBreak) {
    state.lastKnownAgentStateChange.break = agentState.stateChangeTimestamp || new Date().toISOString();
  } else if (agentState.isReady) {
    state.lastKnownAgentStateChange.ready = agentState.stateChangeTimestamp || new Date().toISOString();
  } else if (agentState.isBackoffice) {
    state.lastKnownAgentStateChange.backoffice = agentState.stateChangeTimestamp || new Date().toISOString();
  } else if (agentState.isOutgoing) {
    state.lastKnownAgentStateChange.outgoing = agentState.stateChangeTimestamp || new Date().toISOString();
  } else if (agentState.isBusy) {
    state.lastKnownAgentStateChange.busy = agentState.stateChangeTimestamp || new Date().toISOString();
  }
};

const updateBusinessProcessStats = (state, bps) => {
  state.BUSINESS_PROCESSES.splice(0, state.BUSINESS_PROCESSES.length);
  state.bpsLastUpdate = moment();
  state.BUSINESS_PROCESSES = bps;
};

const clearStats = state => {
  state.AGENTS = [];
  state.BUSINESS_PROCESSES = [];
  state.agentsLastUpdate = null;
  state.bpsLastUpdate = null;
  state.stateLastUpdate = {
    onlineTime: '',
    readyTime: '',
    busyTime: '',
    breakTime: '',
    backofficeTime: '',
    outgoingTime: ''
  };
};

const addKpiSubscription = (state, { counterTypeId, handlerId, dimensionId = null }) => {
  if (!state.subscribedCounters[counterTypeId]) {
    // eslint-disable-next-line import/no-named-as-default-member
    Vue.set(state.subscribedCounters, counterTypeId, {});
  }
  if (!state.subscribedCounters[counterTypeId][handlerId]) {
    // eslint-disable-next-line import/no-named-as-default-member
    Vue.set(state.subscribedCounters[counterTypeId], handlerId, { refCount: 0, value: null });
  }
  if (dimensionId && !state.subscribedCounters[counterTypeId][handlerId][dimensionId]) {
    // eslint-disable-next-line import/no-named-as-default-member
    Vue.set(state.subscribedCounters[counterTypeId][handlerId], dimensionId, { refCount: 0, value: null });
  }

  if (dimensionId) {
    ++state.subscribedCounters[counterTypeId][handlerId][dimensionId].refCount;
  } else {
    ++state.subscribedCounters[counterTypeId][handlerId].refCount;
  }
};

const removeKpiSubscription = (state, { counterTypeId, handlerId, dimensionId = null }) => {
  if (!state.subscribedCounters[counterTypeId] || !state.subscribedCounters[counterTypeId][handlerId]) {
    return;
  }

  if (dimensionId) {
    if (--state.subscribedCounters[counterTypeId][handlerId][dimensionId].refCount <= 0) {
      Vue.delete(state.subscribedCounters[counterTypeId][handlerId], dimensionId);
    }
    if (
      state.subscribedCounters[counterTypeId][handlerId].refCount === 0 &&
      Object.keys(state.subscribedCounters[counterTypeId][handlerId]).length === 2
    ) {
      Vue.delete(state.subscribedCounters[counterTypeId], handlerId);
    }
  } else if (
    --state.subscribedCounters[counterTypeId][handlerId].refCount === 0 &&
    Object.keys(state.subscribedCounters[counterTypeId][handlerId]).length === 2
  ) {
    Vue.delete(state.subscribedCounters[counterTypeId], handlerId);
  } else if (Object.keys(state.subscribedCounters[counterTypeId][handlerId]).length > 2) {
    state.subscribedCounters[counterTypeId][handlerId].value = null;
  }

  if (Object.keys(state.subscribedCounters[counterTypeId]).length === 0) {
    Vue.delete(state.subscribedCounters, counterTypeId);
  }
};

const clearLastKnownAgentStateChangeTimestamps = state => {
  // eslint-disable-next-line import/no-named-as-default-member
  Vue.set(state, 'lastKnownAgentStateChange', {});
};

const setKpiSubscriptionCounter = (state, counter) => {
  const { counterTypeId, handlerId, dimensionId = null } = counter;

  if (!state.subscribedCounters[counterTypeId] || !state.subscribedCounters[counterTypeId][handlerId]) {
    return;
  }

  switch (counterTypeId) {
    case agentSubscriptions.loginTime.counterTypeId:
      state.lastKnownAgentStateChange.online = counter.value;
      break;
    case agentSubscriptions.readyTime.counterTypeId:
      state.lastKnownAgentStateChange.ready = new Date().toISOString();
      break;
    case agentSubscriptions.busyTime.counterTypeId:
      state.lastKnownAgentStateChange.busy = new Date().toISOString();
      break;
    case agentSubscriptions.breakTime.counterTypeId:
      state.lastKnownAgentStateChange.break = new Date().toISOString();
      break;
    case agentSubscriptions.backofficeTime.counterTypeId:
      state.lastKnownAgentStateChange.backoffice = new Date().toISOString();
      break;
    case agentSubscriptions.outgoingTime.counterTypeId:
      state.lastKnownAgentStateChange.outgoing = new Date().toISOString();
      break;
  }

  if (dimensionId && state.subscribedCounters[counterTypeId][handlerId][dimensionId]) {
    state.subscribedCounters[counterTypeId][handlerId][dimensionId].value = counter.value;
    state.subscribedCounters[counterTypeId][handlerId][dimensionId].secondaryValue = counter.secondaryValue;
  } else if (state.subscribedCounters[counterTypeId][handlerId]) {
    const sum = moment.duration(counter.value).add(moment.duration(counter.secondaryValue));
    const sumFormatted = EcsAgentStatsMath.stringifyDuration(sum);
    state.subscribedCounters[counterTypeId][handlerId].value = sumFormatted || counter.value;
    state.subscribedCounters[counterTypeId][handlerId].secondaryValue = counter.secondaryValue;
  } else {
    throw new Error('Counter not found');
  }
};

export default {
  setAgentStatValue,
  updateAgentsStats,

  setLastKnownAgentStateChangeTimestamp,
  clearLastKnownAgentStateChangeTimestamps,
  setBusinessProcessStatValue,
  updateBusinessProcessStats,

  addKpiSubscription,
  removeKpiSubscription,
  setKpiSubscriptionCounter,

  clearStats
};
